'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/navigation';
import { servicesAPI, departmentsAPI } from '@/lib/api';
import { usePathname } from 'next/navigation';
import { useSettings } from '@/lib/settings-context';
import {
    Bars3Icon,
    XMarkIcon,
    PhoneIcon,
    ChevronDownIcon,
} from '@heroicons/react/24/outline';

const navLinks = [
    { label: 'Home', href: '/' },
    {
        label: 'About',
        href: '/about',
        children: [
            { label: 'History & Mission', href: '/about' },
            { label: 'Leadership', href: '/about#leadership' },
            { label: 'Organizational Structure', href: '/about#structure' },
        ],
    },
    {
        label: 'Services',
        href: '/services',
        children: [
            { label: 'All Services', href: '/services' },
            { label: 'Psychiatry Services', href: '/services/psychiatry-services' },
            { label: 'Emergency Services (24/7)', href: '/services/emergency-services' },
            { label: 'Internal Medicine', href: '/services/internal-medicine' },
            { label: 'Neurology Services', href: '/services/neurology-services' },
            { label: 'Laboratory & Radiology', href: '/services/laboratory-services' },
            { label: 'Pharmacy Services', href: '/services/outpatient-pharmacy' },
            { label: 'Clinical Psychology', href: '/services/clinical-psychology' },
            { label: 'MCH & Family Planning', href: '/services/mch-services' },
        ],
    },
    {
        label: 'Departments',
        href: '/departments',
        children: [
            { label: 'All Departments', href: '/departments' },
            { label: 'Psychiatry Department', href: '/departments/psychiatry' },
            { label: 'Internal Medicine', href: '/departments/internal-medicine' },
            { label: 'Neurology', href: '/departments/neurology' },
            { label: 'Radiology', href: '/departments/radiology' },
            { label: 'Laboratory', href: '/departments/laboratory' },
        ],
    },
    { label: 'Research', href: '/research' },
    { label: 'CPD Training', href: '/cpd' },
    { label: 'Clinical Attachment', href: '/clinical-attachment' },
    {
        label: 'News',
        href: '/news',
        children: [
            { label: 'News & Articles', href: '/news' },
            { label: 'Announcements', href: '/news?type=ANNOUNCEMENT' },
            { label: 'Events', href: '/news?type=EVENT' },
        ],
    },
    { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
    const { getSetting } = useSettings();
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const [links, setLinks] = useState(navLinks);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const pathname = usePathname();

    // Dynanmic Identity Parameter
    const siteNameLong = getSetting('site_name', 'Amanuel Mental Specialized Hospital');
    const siteNameShort = 'AMSH';
    const sitePhone = getSetting('contact_phone', '+251-111-868-53-85');
    const siteEmail = getSetting('contact_email', 'info@amsh.gov.et');
    const siteHours = getSetting('working_hours', 'Mon - Fri: 2:30 AM - 10:00 AM');
    const siteLogo = getSetting('site_logo', '');

    useEffect(() => {
        Promise.all([
            servicesAPI.getAll().catch(() => ({ data: [] })),
            departmentsAPI.getAll().catch(() => ({ data: [] }))
        ]).then(([servicesRes, departmentsRes]) => {
            const services = Array.isArray(servicesRes.data) ? servicesRes.data : Array.isArray(servicesRes.data?.services) ? servicesRes.data.services : [];
            const departments = Array.isArray(departmentsRes.data) ? departmentsRes.data : Array.isArray(departmentsRes.data?.departments) ? departmentsRes.data.departments : [];

            if (services.length > 0 || departments.length > 0) {
                setLinks(prev => prev.map(link => {
                    if (link.label === 'Services' && services.length > 0) {
                        return {
                            ...link,
                            children: [
                                { label: 'All Services', href: '/services' },
                                ...services.slice(0, 10).map((s: any) => ({ label: s.name, href: `/services/${s.slug}` }))
                            ]
                        };
                    }
                    if (link.label === 'Departments' && departments.length > 0) {
                        return {
                            ...link,
                            children: [
                                { label: 'All Departments', href: '/departments' },
                                ...departments.slice(0, 12).map((d: any) => ({ label: d.name, href: `/departments/${d.slug}` }))
                            ]
                        };
                    }
                    return link;
                }));
            }
        });
    }, []);

    const handleMouseEnter = (label: string) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setActiveDropdown(label);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setActiveDropdown(null);
        }, 200);
    };

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => { setIsOpen(false); }, [pathname]);

    const isActive = (href: string) => pathname === href || pathname.startsWith(href + '/');

    return (
        <>
            {/* TOP BAR */}
            <div className="bg-blue-900 text-white text-[11px] font-bold py-2 hidden md:block border-b border-white/5">
                <div className="container-custom flex items-center justify-center">
                    <div className="flex items-center gap-4">
                        <a href={`tel:${sitePhone.replace(/\s/g, '')}`} className="hover:text-cyan-300 transition-colors tracking-widest uppercase">
                            {sitePhone}
                        </a>
                        <span className="text-blue-400 font-normal">|</span>
                        <a href={`mailto:${siteEmail}`} className="hover:text-cyan-300 transition-colors tracking-widest opacity-90">
                            {siteEmail}
                        </a>
                        <span className="text-blue-400 font-normal">|</span>
                        <span className="text-blue-300 tracking-wider uppercase">{siteHours}</span>
                    </div>
                </div>
            </div>

            {/* MAIN NAVBAR */}
            <header className={`sticky top-0 z-50 transition-all duration-500 ${scrolled
                ? 'glass-nav py-2'
                : 'bg-white/90 backdrop-blur-md py-4'
                }`}>
                <div className="container-custom">
                    <div className="flex items-center justify-between h-16">

                        {/* LOGO - DYNAMIC FLOATING EFFECT */}
                        <a href="/" className="flex items-center gap-4 flex-shrink-0 group relative z-[60] -ml-16 lg:-ml-24">
                            <div className={`relative overflow-hidden transition-all duration-700 ease-in-out flex items-center justify-center rounded-full border-[4px] border-white bg-white shadow-[0_10px_30px_rgba(0,0,0,0.1)]
                                ${scrolled
                                    ? 'w-14 h-14'
                                    : 'w-28 h-28 -mb-14 scale-100 translate-y-2'}`}
                            >
                                {siteLogo ? (
                                    <img
                                        src={siteLogo}
                                        alt="Institutional Logo"
                                        className="w-full h-full object-contain p-2"
                                    />
                                ) : (
                                    <video
                                        src="/images/PixVerse_V5.6_Image_Text_360P_Create_a_premium.mp4"
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        className="w-full h-full object-cover rounded-full"
                                    />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent pointer-events-none" />
                            </div>
                            {/* Text beside the logo */}
                            <div className="flex flex-col">
                                <div className={`font-black text-blue-900 leading-none uppercase tracking-tight transition-all duration-500 ${scrolled ? 'text-xl' : 'text-2xl'}`}>{siteNameShort}</div>
                                <div className={`text-gray-600 font-extrabold leading-tight transition-all duration-500 ${scrolled ? 'text-[11px]' : 'text-xs'}`}>አማኑኤል የአእምሮ ስፔሻላይዝድ ሆስፒታል</div>
                                <div className={`text-gray-400 font-bold leading-tight transition-all duration-500 ${scrolled ? 'text-[10px]' : 'text-[11px]'}`}>{siteNameLong}</div>
                            </div>
                        </a>

                        <div className="flex-1" />

                        {/* DESKTOP NAVIGATION */}
                        <nav className="hidden lg:flex items-center gap-1">
                            {links.map((link) => (
                                <div
                                    key={link.href}
                                    className="relative group"
                                    onMouseEnter={() => link.children && handleMouseEnter(link.label)}
                                    onMouseLeave={() => link.children && handleMouseLeave()}
                                >
                                    <a
                                        href={link.href}
                                        className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${isActive(link.href) && link.href !== '/'
                                            ? 'text-blue-900 bg-blue-50'
                                            : isActive(link.href) && link.href === '/'
                                                ? 'text-blue-900 bg-blue-50'
                                                : 'text-gray-700 hover:text-blue-900 hover:bg-blue-50'
                                            }`}
                                    >
                                        {link.label}
                                        {link.children && (
                                            <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${activeDropdown === link.label ? 'rotate-180' : ''}`} />
                                        )}
                                    </a>

                                    {/* DROPDOWN */}
                                    {link.children && activeDropdown === link.label && (
                                        <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden py-2 animate-fade-in z-50">
                                            {link.children.map((child) => (
                                                <a
                                                    key={child.href}
                                                    href={child.href}
                                                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:text-blue-900 hover:bg-blue-50 transition-colors"
                                                >
                                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-900/40" />
                                                    {child.label}
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </nav>

                        {/* MOBILE MENU BUTTON */}
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                            aria-label="Toggle menu"
                        >
                            {isOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {/* MOBILE MENU */}
                {isOpen && (
                    <div className="lg:hidden border-t border-gray-100 animate-fade-in">
                        <div className="container-custom py-4 space-y-1 max-h-[calc(100vh-80px)] overflow-y-auto">

                            {links.map((link) => (
                                <div key={link.href}>
                                    <a
                                        href={link.href}
                                        className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${isActive(link.href) ? 'bg-blue-900 text-white' : 'text-gray-700 hover:bg-blue-50 hover:text-blue-900'
                                            }`}
                                    >
                                        {link.label}
                                    </a>
                                    {link.children && (
                                        <div className="ml-4 mt-1 space-y-1">
                                            {link.children.map((child) => (
                                                <a
                                                    key={child.href}
                                                    href={child.href}
                                                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-blue-900 rounded-lg hover:bg-blue-50 transition-colors"
                                                >
                                                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                                                    {child.label}
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}

                        </div>
                    </div>
                )}
            </header>
        </>
    );
}
